import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useRef,
  MouseEvent,
  useMemo,
} from "react";
import { Menu, MenuItem, Divider, ListItemIcon, ListItemText, ListSubheader } from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import SnTab from "@shared/components/SnTab";
import { menuInfo } from "@shared/type/CommonType";
import SnNavBtn from "@shared/components/SnNavBtn";
import SnFormControls from "@shared/components/SnFormControls";
import {
  SnSortableFormArea,
  SnSortableFormControl,
  sortIdOf,
} from "@shared/components/SnSortableFormControls";
import { SnControlSelector } from "@shared/components/SnControlSelector";
import { showPopupModal } from "@widgets/CommonModal";
import { global } from "@shared/libs/Global";
import { fnSave, fnGetDataSet } from "@shared/api/restApi";
import { showDialog } from "@widgets/CommonDialog";

interface SharedFormProps {
  data: any;
  controls: any[];
  width?: number;
  // 화면별로 폼 grid column 폭 정책을 오버라이드한다. 기본값은 기존 13컬럼 최소폭 패턴을 유지한다.
  gridTemplateColumns?: string;
  gridref?: any;
  onButtonClick?: (sender: string) => void;
  onValueChaged?: (data: any, param: string, formData: any) => void;
  title?: string;
  header?: React.ReactNode;
  /** 폼 항목들 바로 뒤(같은 그리드 흐름)에 붙일 내용. 마지막 항목 아래에 이어서 그려진다. */
  footer?: React.ReactNode;
  menuInfo?: menuInfo;
  /** 행 선택/조회 시 첫 컨트롤 자동 포커스 여부. 기본 true. false 면 포커스를 옮기지 않는다. */
  autoFocus?: boolean;
  /** sys_contextmenu 항목(비 common) 클릭 시 화면으로 전달 — 드릴다운 등 */
  onContextMenu?: (params: {
    id: string;
    menu: string;
    functionId: string;
  }) => void;
}

type InputEls = {
  el: HTMLElement | null;
  isFocusable: boolean;
};

/** 우클릭 메뉴 제목 줄 — 작고 옅게 (2026-10-02, SearchPanel 과 같다) */
const menuHeadSx = { lineHeight: "28px", fontSize: 12, color: "#7d7e80", bgcolor: "transparent" };

const SharedForm = forwardRef(
  (
    {
      data,
      controls,
      width,
      gridTemplateColumns,
      gridref,
      onButtonClick,
      title,
      header,
      footer,
      menuInfo,
      onValueChaged,
      autoFocus = true,
      onContextMenu,
    }: SharedFormProps,
    ref
  ) => {
    const [formData, setFormData] = useState<any>(data);
    const [visibleControls, setVisibleControls] = useState<any[]>([]);
    const [hiddenControls, setHiddenControls] = useState<any[]>([]);
    const [editCons, setEditCons] = useState<any[]>();
    const [editCon, setEditCon] = useState<any>();
    const [requireds, setRequireds] = useState<any>({});
    const [groups, setGroups] = useState<any[]>();
    const [filters, setFilters] = useState<any[]>();
    const [filter, setFilter] = useState<any>();
    const formRef = useRef<HTMLDivElement>(null); // form 영역을 참조
    const [menuPosition, setMenuPosition] = useState<{
      mouseX: any;
      mouseY: any;
    } | null>(null);
    const [menuItems, setMenuItems] = useState<
      Array<{ name: string; group: string; item: any }>
    >([]);
    const isAddContext = useRef<boolean>(false);
    const refs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    // const inputEls = useRef<HTMLInputElement[]>([]); // sharedForm에 있는 input element 모음
    const inputEls = useRef<InputEls[]>([]);
    const [scrollTop, setScrollTop] = useState(0);
    const [popupCons, setPopupCons] = useState<any>({}); /* */
    const { userInfo } = global();
    /* 배치 편집에 들어가기 직전의 칸 목록 — [편집 취소] 때 되돌린다 (2026-10-02) */
    const layoutSnapshotRef = useRef<{ visible: any[]; hidden: any[] } | null>(null);
    const [isDragOn, setIsDragOn] = useState(false);
    const [focus, setFocus] = useState<boolean>();

    const fetchControl = async () => {
      try {
        const eidtConFilters = controls.filter(
          (control) =>
            control.dc_editcon &&
            control.dc_editcon.length > 0 &&
            control.dc_editcon.trim() != ""
        );
        setEditCons(eidtConFilters);

        const filterFilters = controls.filter(
          (control) =>
            control.dc_filter &&
            control.dc_filter.length > 0 &&
            control.dc_filter.trim() != ""
        );
        setFilters(filterFilters);

        const groupFilters = controls.filter(
          (control) => control.tp_ctrl === "SnGroupLabel"
        );
        setGroups(groupFilters);

        const requiredsFilter = controls.filter(
          (control) => control.yn_required === "Y"
        );
        let tempreq = Object.assign({});
        requiredsFilter.map((req) => {
          tempreq[req.cd_param] = req.nm_label;
        });
        setRequireds(tempreq);
        const visibleFilters = controls.filter(
          (control) => control.fg_visible && control.fg_visible.trim() == "Y"
        );
        setVisibleControls(visibleFilters);
        const hiddenFilters = controls.filter(
          (control) => control.fg_visible && control.fg_visible.trim() != "Y"
        );
        setHiddenControls(hiddenFilters);
      } catch (error: any) {}
    };

    const fetchData = async () => {
      try {
        requestAnimationFrame(() => {
          setChangeCon(data);
          setFormData(data); // 상태 갱신 (폼 렌더링 유발)
          setFocus(true);
        });
      } catch (error: any) {}
    };

    // controls가 data보다 늦게 들어오는 경우에도 dc_editcon/dc_filter를 다시 계산한다.
    useEffect(() => {
      fetchData();
    }, [data, controls]);

    // 26.09.23 kdy 그리드 연동 폼(gridref)은 신규행(state === "insert")일 때만 첫 컨트롤 자동 포커스.
    // 조회된 행 사이를 이동할 때마다 폼이 포커스를 가져가면 아래로 스크롤해 둔 폼이 맨 위로 튀어 올라간다.
    useEffect(() => {
      if (focus) {
        requestAnimationFrame(() => {
          const isGridLinked = !!gridref?.current;
          const isInsertRow = data?.state === "insert";
          if (autoFocus && (!isGridLinked || isInsertRow)) {
            focusInputByIndex(0);
          }
          setFocus(false);
        });
      }
    }, [focus]);

    useEffect(() => {
      fetchControl();
    }, [controls]);

    useEffect(() => {
      if (groups) {
        const first = groups[0]?.no_line;
        if (first) {
          groupButtonClick(`${first}`);
        }
      }
    }, [groups, refs]);

    const onTextValueChanged = useCallback(
      async (param: string, data: string) => {
        const prevValue = formData?.[param] ?? "";
        if (prevValue === data) return;

        const baseFormData = { ...formData, [param]: data };
        setFormData(baseFormData);
        setChangeCon(baseFormData);

        const updatedFields =
          (await onValueChaged?.(data, param, formData)) || {};
        const finalFormData = { ...baseFormData, ...updatedFields };

        setFormData(finalFormData);
        setChangeCon(finalFormData);
        gridref?.current?.sharedCellValueChanged?.(finalFormData);
      },
      [formData]
    );

    const onNumberValueChanged = useCallback(
      async (param: string, data: number) => {
        const prevValue = formData?.[param] ?? 0;
        if (prevValue === data) return;

        const baseFormData = { ...formData, [param]: data };
        setFormData(baseFormData);
        setChangeCon(baseFormData);

        const updatedFields =
          (await onValueChaged?.(data, param, formData)) || {};
        const finalFormData = { ...baseFormData, ...updatedFields };

        setFormData(finalFormData);
        setChangeCon(finalFormData);
        gridref?.current?.sharedCellValueChanged?.(finalFormData);
      },
      [formData]
    );

    const onPeriodValueChanged = useCallback(
      async (param: string, start: string, end: string) => {
        const [startKey, endKey] = param.split(",").map((p) => p.trim());
        const prevStart = formData?.[startKey] ?? "";
        const prevEnd = formData?.[endKey] ?? "";

        if (prevStart === start && prevEnd === end) return;

        const baseFormData = { ...formData, [startKey]: start, [endKey]: end };
        setFormData(baseFormData);
        setChangeCon(baseFormData);

        const updatedFields =
          (await onValueChaged?.({ start, end }, param, formData)) || {};
        const finalFormData = { ...baseFormData, ...updatedFields };

        setFormData(finalFormData);
        setChangeCon(finalFormData);
        gridref?.current?.sharedCellValueChanged?.(finalFormData);
      },
      [formData]
    );

    const onCodeValueChanged = useCallback(
      async (
        code: string,
        data: string,
        name: string,
        data2: string,
        rowData?: string
      ) => {
        const prevCode = formData?.[code] ?? "";
        if (prevCode === data) return;

        let baseFormData = {
          ...formData,
          [code]: data,
          [name]: data2,
        };

        setFormData(baseFormData);
        setChangeCon(baseFormData);

        if (rowData) {
          const updatedFields =
            (await onValueChaged?.(rowData, code, formData)) || {};
          const finalFormData = { ...baseFormData, ...updatedFields };

          setFormData(finalFormData);
          setChangeCon(finalFormData);
          gridref?.current?.sharedCellValueChanged?.(finalFormData);
        } else {
          gridref?.current?.sharedCellValueChanged?.(baseFormData);
        }
      },
      [formData]
    );

    const setChangeCon = useCallback(
      (change: any) => {
        const editObj: Record<string, any> = {};
        const filterObj: Record<string, any> = {};
        const visibleObj: Record<string, boolean> = {};

        // editCons/filters state는 controls effect 이후에 채워지므로,
        // controls와 data가 같은 렌더에 들어오는 팝업에서는 첫 setChangeCon(data) 시점에 비어 있다.
        // SnForm과 동일하게 controls에서 직접 계산해 첫 표시부터 dc_editcon/dc_filter가 반영되게 한다.
        const editCons = controls.filter(
          (control) =>
            control.dc_editcon &&
            control.dc_editcon.length > 0 &&
            control.dc_editcon.trim() != ""
        );
        const filters = controls.filter(
          (control) =>
            control.dc_filter &&
            control.dc_filter.length > 0 &&
            control.dc_filter.trim() != ""
        );

        // editCon 처리
        if (editCons && change) {
          editCons.forEach((con) => {
            let conData = con.dc_editcon;

            if (change.state === "insert") {
              conData = conData.replace("new", "false");
            } else {
              conData = conData.replace("new", "true");
            }

            Object.keys(change).forEach((key) => {
              conData = conData.replaceAll(key, change[key]);
            });

            editObj[con.cd_param] = conData;
          });
          setEditCon(editObj);
        }

        // filter 처리
        if (filters && change) {
          filters.forEach((fil) => {
            let filData = fil.dc_filter;
            Object.keys(change).forEach((key) => {
              filData = filData.replaceAll(`{${key}}`, change[key]);
            });
            filterObj[fil.cd_param] = filData;
          });
          setFilter(filterObj);
        }

        // visible 처리
        if (visibleControls) {
          visibleControls.forEach((control) => {
            const raw = editObj?.[control.cd_param];
            if (raw && change.hasOwnProperty(control.cd_param)) {
              try {
                visibleObj[control.cd_param] = Boolean(
                  new Function(`return ${raw};`)()
                );
              } catch (e) {
                visibleObj[control.cd_param] = false;
                console.error("eval error in setChangeCon:", raw, e);
              }
            } else {
              visibleObj[control.cd_param] = false;
            }
          });
        }

        // 필요시 visibleObj 저장 또는 상태 처리 가능
      },
      [controls, visibleControls]
    );

    const setReadOnly = useCallback(
      (param: string) => {
        const expr = editCon?.[param];
        if (!expr) return false;

        try {
          return Boolean(new Function(`return (${expr});`)());
        } catch (e) {
          console.error("eval error in setReadOnly:", expr, e);
          return false;
        }
      },
      [editCon]
    );

    useImperativeHandle(ref, () => ({
      focusInputByIndex,
      applyPopupConditions: (popupConditions: any) =>
        setPopupCons(popupConditions),
      // 현재 폼 입력값(내부 formData). gridref 없이 SharedForm을 단독 입력폼으로 쓸 때 값을 꺼내는 용도.
      getFormdata: () => formData,
      // yn_required = 'Y' 컨트롤의 값이 비어 있으면 안내창을 띄우고 false 를 돌려준다. (SnForm 과 같은 이름)
      // cd_param 이 "a,b"(기간류)면 두 키 모두 검사하고, SnNumberBox 는 0 도 미입력으로 본다.
      requiredValuesCheck: () => {
        const requiredControls = (controls || []).filter(
          (control: any) => control.yn_required === "Y"
        );
        for (const required of requiredControls) {
          const keys = (required.cd_param || "")
            .toString()
            .split(",")
            .map((key: string) => key.trim())
            .filter(Boolean);
          for (const key of keys) {
            const value = formData?.[key];
            const isEmpty =
              required.tp_ctrl === "SnNumberBox"
                ? value == null || value === "" || Number(value) === 0
                : value == null || value.toString().trim() === "";
            if (isEmpty) {
              showDialog("alter", `${required.nm_label}은(는) 필수값입니다.`);
              const focusIndex = (visibleControls || []).findIndex(
                (control: any) => control.cd_param === required.cd_param
              );
              if (focusIndex >= 0) focusInputByIndex(focusIndex);
              return false;
            }
          }
        }
        return true;
      },
    }));

    const gridContainerStyle = {
      display: "grid",
      // 좁은 패널(화면 분할)에서 최소폭 때문에 가로 스크롤이 생기는 화면은 이 값을 넘겨 줄인다.
      gridTemplateColumns:
        gridTemplateColumns || "repeat(13, minmax(50px, 1fr))",
      gridAutoRows: "32px",
      rowGap: "10px",
      columnGap: "15px",
      padding: "10px",
      overflowY: "auto" as "auto",
      flex: "1 1 auto",
      // border: "1px solid rgb(186,191,199)",
      position: "relative",
    } as const;

    const moveControl = (fromIdx: number, toIdx: number) => {
      setVisibleControls((prev) => {
        const updated = [...prev];
        const [moved] = updated.splice(fromIdx, 1); // 1. 기존 위치에서 제거
        updated.splice(toIdx, 0, moved); // 2. 목표 인덱스에 삽입
        return updated;
      });
    };

    const removeControl = (Idx: number, Id: number) => {
      setVisibleControls((prev) => {
        const updated = [...prev];
        updated.splice(Idx, 1); // 1. 기존 위치에서 제거
        return updated;
      });

      // controls에서 no_line 값이 Id와 같은 control을 찾아 hiddenControls에 추가
      const controlToHide = controls.find((control) => control.no_line === Id);
      if (controlToHide) {
        setHiddenControls((prev) => [...prev, controlToHide]);
      }
    };

    // sys_contextmenu(폼 우클릭 메뉴) 로드 — SnForm과 동일 규약(하위호환)
    useEffect(() => {
      if (isAddContext.current || !controls || controls.length === 0) return;
      isAddContext.current = true;
      const loadContextMenu = async () => {
        try {
          const params = {
            queryId: "getContextMeneu",
            id: "CommonMapper",
            params: {
              cd_menu: controls[0]?.cd_menu || "",
              cd_firm: userInfo.cd_firm,
              cd_ctrl: controls[0]?.cd_form || "",
            },
          };
          const data: any = await fnGetDataSet(params);
          if (!data || data.length === 0 || !Array.isArray(data[0])) {
            setMenuItems([]);
            return;
          }
          setMenuItems(
            data[0].map((item: any) => ({
              name: item.nm_contextmenu,
              group: item.no_group,
              item,
            }))
          );
        } catch {
          // 컨텍스트메뉴 조회 실패는 폼 동작에 영향 없어야 하므로 빈 메뉴로 폴백
          setMenuItems([]);
        }
      };
      loadContextMenu();
    }, [controls]);

    // sys_contextmenu 항목 클릭 → 화면 onContextMenu로 전달(드릴다운 등)
    const handleContextMenu = (item: any) => {
      handleMenuClose();
      if (!item?.cd_contextmenu) return;
      onContextMenu?.({
        id: item.cd_contextmenu,
        menu: item.cd_menu_target,
        functionId: item.cd_function,
      });
    };

    const handleRightClick = (event: MouseEvent) => {
      event.preventDefault(); // 기본 메뉴 비활성화

      // 자식 요소 위에서 우클릭을 했을 때는 아무 동작도 하지 않음
      if (formRef.current) {
        const panelArea = formRef.current.getBoundingClientRect(); // panel 영역
        const { clientX: x, clientY: y } = event; // 클릭 좌표

        // 클릭 좌표가 panel 영역 내에 있는지 확인
        const isInsidePanel =
          x >= panelArea.left &&
          x <= panelArea.right &&
          y >= panelArea.top &&
          y <= panelArea.bottom;

        if (isInsidePanel) {
          // 모든 자식 요소의 위치를 가져와서 클릭된 위치와 비교
          const children = Array.from(formRef.current.children);
          const isOnChild = children.some((child) => {
            const childRect = (child as HTMLElement).getBoundingClientRect();
            return (
              x >= childRect.left &&
              x <= childRect.right &&
              y >= childRect.top &&
              y <= childRect.bottom
            );
          });

          if (!isOnChild && canOpenMenu) {
            // 자식 요소 위가 아닌 빈 공간 클릭 -> 메뉴 열기. 보여 줄 항목이 없으면 띄우지 않는다 (2026-10-02)
            setMenuPosition({ mouseX: x, mouseY: y });
          }
        } else {
          handleMenuClose(); // panel 외부에서 클릭하면 메뉴 닫기
        }
      }
    };

    const handleMenuClose = () => {
      setMenuPosition(null);
    };

    /* 배치 편집은 SYSTEM 계정만. 편집 중이면 늘 메뉴가 있다 · 화면별 우클릭 항목(sys_contextmenu)이 있으면 연다.
       보여 줄 항목이 없으면(일반 사용자 평소) 메뉴를 띄우지 않는다 (2026-10-02 — 쓰지 않던 Option 1 · 2 · 3 삭제) */
    const canEditLayout = global.getState().userInfo.cd_user === "SYSTEM";
    const canOpenMenu = isDragOn || canEditLayout || menuItems.length > 0;

    const setFormControl = () => {
      setIsDragOn((prev) => !prev); // 상태로 토글 → 리렌더 보장
      handleMenuClose();
    };

    /* 항목 선택 — 조회조건과 같은 체크 창을 띄운다. 전에는 창 없이 편집 모드만 꺼졌다 (2026-10-06).
       필수 항목은 체크를 풀 수 없다 — X 로 지울 수 없는 것과 같다. 숨기면 입력할 칸이 없어 저장할 수 없다 */
    const addFormControl = () => {
      const Controls = [
        ...visibleControls.map((c) => ({ ...c, fg_visible: "Y" })),
        ...hiddenControls.map((c) => ({ ...c, fg_visible: "N" })),
      ];
      showPopupModal({
        title: "항목 선택",
        okayBtn: false,
        closeBtn: false,
        width: "250px",
        height: "400px",
        children: (
          <SnControlSelector
            initialControls={Controls}
            isLocked={(c) => c.yn_required === "Y"}
            onApply={(visible, hidden) => {
              setVisibleControls(visible || []);
              setHiddenControls(hidden || []);
            }}
          />
        ),
      });
      handleMenuClose();
    };

    const saveFormControl = async () => {
      const hasCdModule = controls.some(
        (control) =>
          control.cd_module !== undefined && control.cd_module !== null
      );
      const isNew = controls.some(
        (control) => control.cd_user !== userInfo?.cd_user
      );

      const mapControl = (control: any, idx: number, visible: boolean) => ({
        cd_firm: control.cd_firm,
        cd_menu: control.cd_menu,
        cd_form: control.cd_form,
        no_line: control.no_line,
        cd_user: userInfo?.cd_user,
        qt_rowspan: control.qt_rowspan,
        qt_colspan: control.qt_colspan,
        fg_visible: visible ? "Y" : "N",
        no_seq: visible ? idx + 1 : visibleControls.length + idx + 1,
        ...(isNew && {
          tp_ctrl: control.tp_ctrl,
          nm_label: control.nm_label,
          cd_value: control.cd_value,
          tp_format: control.tp_format,
          cd_param: control.cd_param,
          cd_clas: control.cd_clas,
          dc_sql: control.dc_sql,
          cd_popup: control.cd_popup,
          yn_required: control.yn_required,
          dc_editcon: control.dc_editcon,
          cd_displaycolumn: control.cd_displaycolumn,
          dc_filter: control.dc_filter,
        }),
        ...(hasCdModule && { cd_module: control.cd_module }),
      });

      const vControls = visibleControls.map((control, idx) =>
        mapControl(control, idx, true)
      );
      const hControls = hiddenControls.map((control, idx) =>
        mapControl(control, idx, false)
      );

      const updatedControls = [...vControls, ...hControls];
      const save = isNew
        ? { insert: updatedControls }
        : { update: updatedControls };

      const SaveData = !hasCdModule
        ? { sys_formctrl: save }
        : { sys_popupctrl: save };

      handleMenuClose();
      /* 저장되면 보통 모드로 돌아간다. 전에는 저장 뒤에도 편집 모드로 남아 [취소]를 한 번 더 눌러야 했다 (2026-10-02) */
      const saved = await fnSave(
        !hasCdModule
          ? "SN_SYS_BAS_REG_006_SAVE_01"
          : "SN_SYS_BAS_REG_008_SAVE_01",
        SaveData
      );
      if (saved !== false) {
        layoutSnapshotRef.current = null;
        setIsDragOn(false);
      }
    };

    const handleMenuRightClick = (event: React.MouseEvent) => {
      event.preventDefault();
      handleMenuClose();
      handleRightClick(event);
    };

    const groupButtonClick = (index: any) => {
      const target = refs.current[index];
      if (target && formRef.current) {
        target.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      }
    };

    const setRef = (key: any, node: HTMLDivElement | null) => {
      if (node) {
        refs.current[key] = node; // key를 기준으로 DOM 요소를 저장
      }
    };

    const setInputRef = (
      el: HTMLElement | null,
      index: number,
      isReadOnly: boolean
    ) => {
      inputEls.current[index] = {
        el,
        isFocusable: !isReadOnly,
      };
    };

    const focusInputByIndex = async (startIndex: number) => {
      let index = startIndex;
      while (index < inputEls.current.length) {
        const input = inputEls.current[index]?.el;
        const isFocusable = inputEls.current[index]?.isFocusable;
        if (input && isFocusable) {
          input.focus();
          break;
        }
        index++;
      }
    };

    const handleMoveFocus = (fromIndex: number, target: string) => {
      if (target === "current") {
        const el = inputEls.current?.[fromIndex].el;
        if (el) {
          el.focus();
        }
      } else if (target === "next") {
        const list = inputEls.current;
        for (let i = fromIndex + 1; i < list.length; i++) {
          const entry = list[i];
          if (entry?.el && entry.isFocusable) {
            entry.el.focus();
            return;
          }
        }

        // 끝까지 없으면 현재 blur
        list[fromIndex]?.el?.blur?.();
      }
    };

    const renderSharedContent = () => {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            border: "1px solid rgb(186,191,199)",
          }}
        >
          <div className={"flex justify-start"}>
            {groups != null &&
              groups.length > 0 &&
              groups.map((group) => {
                return (
                  <div>
                    <SnNavBtn
                      text={group.nm_label}
                      onClick={() => groupButtonClick(group.no_line)}
                    ></SnNavBtn>
                  </div>
                );
              })}
          </div>

          <div
            style={gridContainerStyle}
            onContextMenu={handleRightClick}
            ref={formRef}
            className="h-[1px]"
          >
            {/* 배치 편집 — 끄는 동안은 이웃 칸만 비켜 나고, 순서는 손을 놓을 때 한 번 바뀐다 (2026-10-06, react-dnd → dnd-kit) */}
            {isDragOn ? (
              <SnSortableFormArea
                ids={(visibleControls || []).map(sortIdOf)}
                onMove={moveControl}
              >
                <>
                  {visibleControls != null &&
                    visibleControls.length > 0 &&
                    visibleControls.map((control, index) => (
                      <SnSortableFormControl
                        key={sortIdOf(control)}
                        sortId={sortIdOf(control)}
                        labletype="SharedForm"
                        isFormPanel={true}
                        control={control}
                        id={control.no_line}
                        index={index}
                        formData={formData}
                        requireds={requireds}
                        popupCons={popupCons}
                        setRef={setRef}
                        setInputRef={setInputRef}
                        handleMoveFocus={handleMoveFocus}
                        onTextValueChanged={onTextValueChanged}
                        onNumberValueChanged={onNumberValueChanged}
                        onPeriodValueChanged={onPeriodValueChanged}
                        onCodeValueChanged={onCodeValueChanged}
                        setReadOnly={setReadOnly}
                        onButtonClick={(param: string) => {
                          onButtonClick != null && onButtonClick(param);
                        }}
                        removeControl={removeControl}
                        filter={filter}
                      />
                    ))}
                </>
              </SnSortableFormArea>
            ) : (
              <>
                {visibleControls != null &&
                  visibleControls.length > 0 &&
                  visibleControls.map((control, index) => (
                    <SnFormControls
                      labletype="SharedForm"
                      isFormPanel={true}
                      control={control}
                      index={index}
                      formData={formData}
                      requireds={requireds}
                      popupCons={popupCons}
                      setRef={setRef}
                      setInputRef={setInputRef}
                      handleMoveFocus={handleMoveFocus}
                      onTextValueChanged={onTextValueChanged}
                      onNumberValueChanged={onNumberValueChanged}
                      onPeriodValueChanged={onPeriodValueChanged}
                      onCodeValueChanged={onCodeValueChanged}
                      setReadOnly={setReadOnly}
                      onButtonClick={(param: string) => {
                        onButtonClick != null && onButtonClick(param);
                      }}
                      filter={filter}
                    />
                  ))}
              </>
            )}
            {footer}
            <Menu
              open={menuPosition !== null}
              onClose={handleMenuClose}
              onContextMenu={handleMenuRightClick}
              anchorReference="anchorPosition"
              anchorPosition={
                menuPosition === null
                  ? undefined
                  : { top: menuPosition.mouseY, left: menuPosition.mouseX }
              }
            >
              {/* 우클릭 메뉴 (2026-10-02) — 색 아이콘 · 제목 줄. 평소 [배치 편집](SYSTEM) + 화면별 항목(sys_contextmenu),
                  편집 중 [항목 선택 · 저장 · 편집 취소]. 동작은 전과 같고 이름 · 모양만 바꿨다 */}
              {!isDragOn && (canEditLayout || menuItems.length > 0) && [
                <ListSubheader key="h" sx={menuHeadSx}>폼</ListSubheader>,
                ...(canEditLayout ? [
                <MenuItem key="edit" dense onClick={() => {
                    layoutSnapshotRef.current = { visible: visibleControls, hidden: hiddenControls };
                    setFormControl();
                  }}>
                  <ListItemIcon><TuneIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText>배치 편집</ListItemText>
                </MenuItem>,
                ] : []),
                ...(canEditLayout && menuItems.length > 0 ? [<Divider key="d0" />] : []),
                ...menuItems.map((menu) => (
                  <MenuItem key={menu.name} dense onClick={() => handleContextMenu(menu.item)}>
                    <ListItemText inset>{menu.name}</ListItemText>
                  </MenuItem>
                )),
              ]}
              {isDragOn && [
                <ListSubheader key="h" sx={menuHeadSx}>폼 편집</ListSubheader>,
                <MenuItem key="pick" dense onClick={() => addFormControl()}>
                  <ListItemIcon><PlaylistAddCheckIcon fontSize="small" color="info" /></ListItemIcon>
                  <ListItemText>항목 선택</ListItemText>
                </MenuItem>,
                <MenuItem key="save" dense onClick={() => saveFormControl()}>
                  <ListItemIcon><SaveIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText>저장</ListItemText>
                </MenuItem>,
                <Divider key="d" />,
                <MenuItem key="cancel" dense onClick={() => {
                    /* 편집 취소 — 배치 편집 전으로 되돌리고 보통 모드로 (2026-10-02) */
                    const snap = layoutSnapshotRef.current;
                    layoutSnapshotRef.current = null;
                    if (snap) {
                      setVisibleControls(snap.visible);
                      setHiddenControls(snap.hidden);
                    }
                    setFormControl();
                  }}>
                  <ListItemIcon><CloseIcon fontSize="small" color="error" /></ListItemIcon>
                  <ListItemText>편집 취소</ListItemText>
                </MenuItem>,
              ]}
            </Menu>
          </div>
        </div>
      );
    };

    return (
      <>
        {title ? (
          <SnTab
            titles={[title]}
            width={"100%"}
            header={header}
            body={[renderSharedContent()]}
          />
        ) : (
          renderSharedContent()
        )}
      </>
    );
  }
);
export default React.memo(SharedForm);
